import { AsyncPipe } from "@angular/common";
import { Component, inject, OnInit } from "@angular/core";
import { RouterLink } from "@angular/router";
import { TranslationKey, UTCDate } from "@ankaadia/ankaadia-shared";
import { translateSafely } from "@ankaadia/ankaadia-shared-frontend";
import {
  ActivityType,
  DashboardAnkaadiaUpdates,
  DashboardBirthdays,
  DashboardCount,
  DashboardCountPerDay,
  MetricType,
} from "@ankaadia/graphql";
import { TranslocoService } from "@jsverse/transloco";
import { ImagePreloadDirective } from "../../shared/directives/image-preload.directive";
import { ThumbnailPipe } from "../../shared/pipes/thumbnail.pipe";
import { SettingsService } from "../../shared/services/settings.service";
import { TranslateDirective } from "../../shared/transloco/translate.directive";
import { AnkaadiaUpdatesComponent } from "./ankaadia-updates/ankaadia-updates.component";
import { DashboardChartComponent } from "./dashboard-chart/dashboard-chart.component";
import { DashboardCounterComponent } from "./dashboard-counter/dashboard-counter.component";
import { DashboardListComponent } from "./dashboard-list/dashboard-list.component";
import { DashboardService } from "./dashboard.service";

interface DashboardMetrics {
  tasksFinished: DashboardCount;
  documentsUploaded: DashboardCount;
  candidatesCreated: DashboardCount;
  documentsExpiring: DashboardCount;
  candidatesLoggedIn: DashboardCountPerDay;
  candidateBirthdays: DashboardBirthdays;
  ankaadiaUpdates: DashboardAnkaadiaUpdates;
}

const metrics: DashboardMetrics = {
  tasksFinished: null,
  documentsUploaded: null,
  candidatesCreated: null,
  documentsExpiring: null,
  candidatesLoggedIn: null,
  candidateBirthdays: null,
  ankaadiaUpdates: null,
};

@Component({
  selector: "app-dashboard",
  templateUrl: "./dashboard.component.html",
  styleUrl: "./dashboard.component.scss",
  imports: [
    TranslateDirective,
    DashboardCounterComponent,
    RouterLink,
    DashboardListComponent,
    ImagePreloadDirective,
    DashboardChartComponent,
    AnkaadiaUpdatesComponent,
    AsyncPipe,
    ThumbnailPipe,
  ],
})
export class DashboardComponent implements OnInit {
  protected readonly ActivityType = ActivityType;
  protected readonly language = this.transloco.getActiveLang();
  protected readonly dayNames = this.getDayNames();
  protected readonly metrics = metrics;
  private readonly settings = inject(SettingsService);
  readonly userName = this.settings.userFirstName;

  constructor(
    private readonly dashboardService: DashboardService,
    private readonly transloco: TranslocoService
  ) {}

  ngOnInit(): void {
    this.update();
  }

  lastDays(n: number | null | undefined): [UTCDate, UTCDate] {
    return [UTCDate.today().addDays(-(n ?? 7)), UTCDate.today()];
  }

  ankaadiaUpdatesSeen(updateSeenEvent: boolean): void {
    if (!updateSeenEvent && this.metrics?.ankaadiaUpdates?.releases) return;
    this.metrics.ankaadiaUpdates = {
      ...this.metrics.ankaadiaUpdates,
      releases: this.metrics.ankaadiaUpdates.releases.map((update) => ({ ...update, isSeen: true })),
    };
  }

  private update(): void {
    this.dashboardService.getMetrics().subscribe((metrics) => {
      this.metrics.tasksFinished = metrics.find(
        (metric): metric is DashboardCount => metric.metricType === MetricType.TasksFinished
      );
      this.metrics.documentsUploaded = metrics.find(
        (metric): metric is DashboardCount => metric.metricType === MetricType.DocumentsUploaded
      );
      this.metrics.candidatesCreated = metrics.find(
        (metric): metric is DashboardCount => metric.metricType === MetricType.CandidatesCreated
      );
      this.metrics.documentsExpiring = metrics.find(
        (metric): metric is DashboardCount => metric.metricType === MetricType.DocumentsExpiring
      );
      this.metrics.candidatesLoggedIn = metrics.find(
        (metric): metric is DashboardCountPerDay => metric.metricType === MetricType.CandidatesLoggedIn
      );
      this.metrics.candidateBirthdays = metrics.find(
        (metric): metric is DashboardBirthdays => metric.metricType === MetricType.CandidateBirthdays
      );
      this.metrics.ankaadiaUpdates = metrics.find(
        (metric): metric is DashboardAnkaadiaUpdates => metric.metricType === MetricType.AnkaadiaUpdates
      );
    });
  }

  private getDayNames(): string[] {
    const dayNames = translateSafely("primeng.dayNamesMin" as TranslationKey) as unknown as string[];
    // eslint-disable-next-line prefer-custom-date-types/prefer-custom-date-types
    const dayOfWeek = new Date().getDay() + 1;
    return [...dayNames.slice(dayOfWeek, dayNames.length), ...dayNames.slice(0, dayOfWeek)];
  }
}
